import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, API, errMsg, getToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { InfoNote, TaskStatusBadge, StatusIcon, DemoBadge } from "@/components/shared";
import { ArrowLeft, Download, GitPullRequest, Loader2, Rocket, ExternalLink, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const RUNNING_STATES = ["approved", "queued", "running", "analysing", "changing", "testing"];

export default function TaskDetail() {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState("");
  const [changesOpen, setChangesOpen] = useState(false);
  const [prodOpen, setProdOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/tasks/${taskId}`);
      setTask(data);
    } catch (e) { toast.error(errMsg(e)); }
    setLoading(false);
  };

  useEffect(() => {
    setLoading(true);
    load();
    const t = setInterval(async () => {
      const { data } = await api.get(`/tasks/${taskId}`).catch(() => ({ data: null }));
      if (data) {
        setTask(data);
        if (!RUNNING_STATES.includes(data.status)) clearInterval(t);
      }
    }, 5000);
    return () => clearInterval(t);
  }, [taskId]); // eslint-disable-line

  const act = async (fn, success) => {
    setBusy(true);
    try {
      await fn();
      toast.success(success);
      setChangesOpen(false);
      setProdOpen(false);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const createPR = () => act(() => api.post(`/tasks/${taskId}/create-pr`), "Pull request created");
  const requestChanges = () => act(() => api.post(`/tasks/${taskId}/request-changes`, { note }), "Changes requested");
  const cancel = () => act(() => api.post(`/tasks/${taskId}/cancel`), "Task cancelled");
  const retry = () => act(() => api.post(`/tasks/${taskId}/retry`), "Retry started");
  const approveProd = () => act(() => api.post(`/tasks/${taskId}/approve-production`, { note }), "Production approved - measurement baseline recorded");

  const fetchPreview = async () => {
    try {
      const { data } = await api.get(`/tasks/${taskId}/preview`);
      setPreview(data);
      if (!data.available) toast.info(data.reason);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const exportPlan = async () => {
    try {
      const res = await fetch(`${API}/tasks/${taskId}/export`, { headers: { Authorization: `Bearer ${getToken()}` } });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `change-plan-${taskId.slice(0, 8)}.md`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Could not export the change plan"); }
  };

  if (loading) return <Skeleton className="h-64 w-full" />;
  if (!task) return <div className="text-sm text-muted-foreground">Task not found.</div>;

  const plan = task.result;
  const isRunning = RUNNING_STATES.includes(task.status);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard/tasks")} data-testid="task-back-button">
          <ArrowLeft className="mr-1 h-4 w-4" /> Tasks
        </Button>
        <h1 className="text-lg font-bold tracking-tight">{task.title}</h1>
        <TaskStatusBadge status={task.status} />
        {task.is_demo_evidence && <DemoBadge />}
        {isRunning && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
      </div>

      {task.status === "failed" && task.error && (
        <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700" data-testid="task-error">{task.error}</div>
      )}

      <div className="flex flex-wrap gap-2">
        {task.status === "preview_ready" && (
          <>
            <Button onClick={() => setProdOpen(true)} data-testid="task-approve-production-button"><Rocket className="mr-1.5 h-4 w-4" /> Approve production</Button>
            <Button variant="outline" onClick={() => setChangesOpen(true)} data-testid="task-request-changes-button">Request changes</Button>
            <Button variant="outline" onClick={createPR} disabled={busy} data-testid="task-create-pr-button">
              <GitPullRequest className="mr-1.5 h-4 w-4" /> Create GitHub PR
            </Button>
          </>
        )}
        {(task.status === "failed" || task.status === "changes_requested") && (
          <Button variant="outline" onClick={retry} disabled={busy} data-testid="task-retry-button"><RefreshCw className="mr-1.5 h-4 w-4" /> Retry</Button>
        )}
        {plan && <Button variant="outline" onClick={exportPlan} data-testid="task-export-button"><Download className="mr-1.5 h-4 w-4" /> Export change plan</Button>}
        {!(task.status === "deployed" || task.status === "cancelled") && (
          <Button variant="ghost" className="text-rose-600 hover:text-rose-700" onClick={cancel} data-testid="task-cancel-button">Cancel task</Button>
        )}
      </div>

      {task.pr && (
        <div className="flex flex-wrap items-center gap-3 rounded-md border p-3">
          <GitPullRequest className="h-4 w-4 text-primary" />
          <span className="text-sm font-medium">PR #{task.pr.number} on {task.pr.repo}</span>
          <a href={task.pr.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-primary" data-testid="task-pr-link">
            View on GitHub <ExternalLink className="h-3 w-3" />
          </a>
          <Button size="sm" variant="outline" className="ml-auto h-7 text-xs" onClick={fetchPreview} data-testid="task-fetch-preview-button">Check preview build</Button>
          {preview?.available && (
            <a href={preview.url} target="_blank" rel="noreferrer" className="text-xs text-primary" data-testid="task-preview-link">Open preview ↗</a>
          )}
        </div>
      )}

      <Tabs defaultValue="changes">
        <TabsList>
          <TabsTrigger value="changes" data-testid="task-tab-changes">Change plan</TabsTrigger>
          <TabsTrigger value="validation" data-testid="task-tab-validation">Validation</TabsTrigger>
          <TabsTrigger value="evidence" data-testid="task-tab-evidence">Evidence & scope</TabsTrigger>
          <TabsTrigger value="history" data-testid="task-tab-history">History</TabsTrigger>
        </TabsList>

        <TabsContent value="changes" className="space-y-3">
          {!plan ? (
            <InfoNote>{isRunning ? "The AI is working on the approved scope. This view updates automatically." : "No change plan yet."}</InfoNote>
          ) : (
            <>
              <Card className="shadow-none"><CardContent className="p-4 text-sm">{plan.summary}</CardContent></Card>
              {(plan.changes || []).map((c, i) => (
                <Card key={i} className="shadow-none" data-testid="task-change-card">
                  <CardContent className="space-y-2 p-4">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant="outline" className="text-[10px]">{c.change_type}</Badge>
                      <span className="text-sm font-semibold">{c.element}</span>
                      <span className="truncate font-mono text-[11px] text-muted-foreground">{c.target}</span>
                    </div>
                    <div className="grid gap-2 md:grid-cols-2">
                      <div className="rounded-md border bg-rose-50/40 p-2">
                        <div className="text-[10px] font-semibold uppercase text-rose-700">Before</div>
                        <pre className="mt-1 whitespace-pre-wrap font-mono text-xs">{c.before || "—"}</pre>
                      </div>
                      <div className="rounded-md border bg-emerald-50/40 p-2">
                        <div className="text-[10px] font-semibold uppercase text-emerald-700">After</div>
                        <pre className="mt-1 whitespace-pre-wrap font-mono text-xs">{c.after || "—"}</pre>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground">{c.reason}</p>
                  </CardContent>
                </Card>
              ))}
              {(plan.internal_link_suggestions || []).length > 0 && (
                <Card className="shadow-none">
                  <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Internal link suggestions</CardTitle></CardHeader>
                  <CardContent className="space-y-1.5">
                    {plan.internal_link_suggestions.map((l, i) => (
                      <div key={i} className="rounded-md border p-2 text-xs">
                        <span className="font-mono">{l.from_page}</span> → <span className="font-mono">{l.to_page}</span>
                        <span className="ml-2 text-muted-foreground">anchor: “{l.anchor}”</span>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>

        <TabsContent value="validation">
          {!task.validation ? <InfoNote>Validation runs automatically after the AI produces a change plan.</InfoNote> : (
            <Card className="shadow-none">
              <CardContent className="space-y-2 p-4">
                <div className="flex items-center gap-2">
                  <StatusIcon ok={task.validation.passed} />
                  <span className="text-sm font-semibold">Gate: {task.validation.gate}</span>
                </div>
                {task.validation.checks.map((c, i) => (
                  <div key={i} className="flex items-start gap-2 rounded-md border p-2" data-testid="validation-check-row">
                    <StatusIcon ok={c.passed} />
                    <div>
                      <div className="text-xs font-medium">{c.check.replaceAll("_", " ")}</div>
                      <div className="text-xs text-muted-foreground">{c.detail}</div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="evidence" className="space-y-3">
          <Card className="shadow-none">
            <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Approved scope</CardTitle></CardHeader>
            <CardContent className="space-y-2 text-xs">
              <div><span className="font-semibold">Allowed:</span> {(task.payload?.allowedChanges || []).join(", ")}</div>
              <div><span className="font-semibold">Protected:</span> {(task.payload?.protectedAreas || []).slice(0, 8).join(" · ")}</div>
              <div><span className="font-semibold">Target URL:</span> <span className="font-mono">{task.payload?.targetURL || "—"}</span></div>
              <div><span className="font-semibold">Risk:</span> {task.payload?.riskLevel}</div>
            </CardContent>
          </Card>
          <Card className="shadow-none">
            <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Search Console evidence</CardTitle></CardHeader>
            <CardContent><pre className="overflow-auto rounded-md bg-muted/40 p-3 font-mono text-[11px]">{JSON.stringify(task.payload?.searchConsoleEvidence, null, 2)}</pre></CardContent>
          </Card>
          <Card className="shadow-none">
            <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Crawl evidence</CardTitle></CardHeader>
            <CardContent><pre className="overflow-auto rounded-md bg-muted/40 p-3 font-mono text-[11px]">{JSON.stringify(task.payload?.crawlEvidence, null, 2)}</pre></CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="history">
          <Card className="shadow-none">
            <CardContent className="space-y-2 p-4">
              {(task.runs || []).map((r) => (
                <div key={r.id} className="flex items-start gap-2 border-b pb-2 text-xs last:border-0" data-testid="task-run-row">
                  <span className="w-36 shrink-0 text-muted-foreground">{new Date(r.created_at).toLocaleString()}</span>
                  <Badge variant="outline" className="text-[10px]">{r.step}</Badge>
                  <span className="text-muted-foreground">{r.status}</span>
                  <span className="min-w-0 break-words">{r.detail?.slice(0, 200)}</span>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={changesOpen} onOpenChange={setChangesOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Request changes</DialogTitle></DialogHeader>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="What should be different?" data-testid="request-changes-note" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setChangesOpen(false)}>Cancel</Button>
            <Button onClick={requestChanges} disabled={busy} data-testid="request-changes-confirm">Submit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={prodOpen} onOpenChange={setProdOpen}>
        <DialogContent data-testid="production-approval-dialog">
          <DialogHeader><DialogTitle>Approve for production</DialogTitle></DialogHeader>
          <div className="space-y-2 text-sm">
            <p>This records the human production approval and starts the 7 / 14 / 28-day measurement windows.</p>
            {task.pr ? (
              <InfoNote>The GitHub PR is never auto-merged. Merge it yourself in GitHub when ready - this approval records the decision and baseline.</InfoNote>
            ) : (
              <InfoNote>No GitHub repository is linked. Apply the exported change plan to your website, then this approval records the baseline.</InfoNote>
            )}
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional note" data-testid="production-approval-note" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setProdOpen(false)}>Cancel</Button>
            <Button onClick={approveProd} disabled={busy} data-testid="production-approval-confirm">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Approve production
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
